// frontend/src/pages/PaymentSuccess.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Shield, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import useEntitlement from "../hooks/useEntitlement";

/**
 * PaymentSuccess.jsx
 * - Confirms paid entitlement via useEntitlement() (Supabase-backed)
 * - Retries entitlement.refresh() for a short window (Stripe → webhook → DB lag)
 *
 * © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
 */

function normalizeTierFromPlanKey(planKey) {
  return String(planKey || "").toLowerCase() === "global_pro" ? "top" : "single";
}

function prettyTierNameFromPlanKey(planKey) {
  const k = String(planKey || "").toLowerCase();
  switch (k) {
    case "pro":
      return "Pro Plan";
    case "enterprise":
      return "Enterprise";
    case "global_pro":
      return "Global Pro";
    case "precision_pass":
      return "Precision Pass";
    default:
      return planKey || "—";
  }
}

function getSafeNext(searchParams) {
  try {
    const raw = searchParams.get("next");
    if (!raw) return "/dashboard";
    const decoded = decodeURIComponent(raw);
    if (typeof decoded !== "string") return "/dashboard";
    if (!decoded.startsWith("/")) return "/dashboard";
    if (decoded.startsWith("//")) return "/dashboard";
    return decoded;
  } catch {
    return "/dashboard";
  }
}

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const sessionId = searchParams.get("session_id");
  const nextPath = useMemo(() => getSafeNext(searchParams), [searchParams]);

  const [status, setStatus] = useState("checking"); // checking | success | failed
  const [planKey, setPlanKey] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const hasProcessedRef = useRef(false);

  const entitlement = useEntitlement?.() || {};
  const loading = Boolean(entitlement.loading);
  const isAuthenticated = Boolean(entitlement.isAuthenticated);
  const isPaid = Boolean(entitlement.isPaid);
  const rawPlanKey = entitlement.rawPlanKey ?? entitlement.planKey ?? null;
  const refresh = typeof entitlement.refresh === "function" ? entitlement.refresh : null;

  useEffect(() => {
    let mounted = true;

    const fail = (msg) => {
      if (!mounted) return;
      setStatus("failed");
      if (msg) toast.error(msg);
    };

    const succeed = (resolvedPlanKey) => {
      if (!mounted) return;
      if (hasProcessedRef.current) return;
      hasProcessedRef.current = true;

      setStatus("success");
      setPlanKey(resolvedPlanKey || null);

      const normalizedTier = normalizeTierFromPlanKey(resolvedPlanKey);

      // Persist tier snapshot for UX (Dashboard can self-heal later)
      try {
        const prevRaw = localStorage.getItem("aifine_user");
        let prev = {};
        try {
          prev = prevRaw ? JSON.parse(prevRaw) : {};
        } catch {
          prev = {};
        }

        localStorage.setItem(
          "aifine_user",
          JSON.stringify({
            ...(prev || {}),
            subscription_tier: normalizedTier,
            raw_tier: resolvedPlanKey || null,
            updated_at_iso: new Date().toISOString(),
          })
        );
      } catch {
        // ignore
      }

      // One-time banner trigger
      try {
        sessionStorage.setItem("aifine_just_upgraded", "1");
      } catch {
        // ignore
      }

      toast.success("Payment successful! Your plan has been upgraded.");

      window.setTimeout(() => {
        navigate(nextPath || "/dashboard", { replace: false });
      }, 900);
    };

    if (!sessionId) {
      fail("Missing payment session id.");
      return () => {
        mounted = false;
      };
    }

    // This route is FREE, but we still need a session to read entitlement.
    if (!loading && !isAuthenticated) {
      const returnTo = `/payment-success${window.location.search || ""}`;
      navigate(`/auth?next=${encodeURIComponent(returnTo)}`, { replace: false });
      return () => {
        mounted = false;
      };
    }

    // If already paid, succeed immediately.
    if (!loading && isPaid) {
      succeed(rawPlanKey);
      return () => {
        mounted = false;
      };
    }

    // Poll entitlement refresh for a short window.
    if (!loading && isAuthenticated && !isPaid) {
      const MAX = 10; // ~25s total
      const intervalMs = 2500;

      let localAttempts = 0;

      const id = window.setInterval(async () => {
        if (!mounted) return;

        localAttempts += 1;
        setAttempts(localAttempts);

        if (localAttempts >= MAX) {
          window.clearInterval(id);
          fail("Payment verification timed out. If you were charged, refresh in 30 seconds.");
          return;
        }

        try {
          if (refresh) await refresh();
        } catch {
          // ignore; next tick will retry
        }

        // If refresh flipped entitlement, the effect will re-run and succeed() above will fire.
      }, intervalMs);

      return () => {
        mounted = false;
        window.clearInterval(id);
      };
    }

    return () => {
      mounted = false;
    };
  }, [sessionId, loading, isAuthenticated, isPaid, rawPlanKey, navigate, refresh, nextPath]);

  const tierName = prettyTierNameFromPlanKey(planKey);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="flex items-center gap-2">
              <Shield className="w-8 h-8 text-slate-900" />
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                AI Fine Protector™
              </span>
            </div>
          </div>

          <div className="mb-6">
            {status === "checking" && (
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                <Loader2 className="w-10 h-10 text-slate-600 animate-spin" />
              </div>
            )}
            {status === "success" && (
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
            )}
            {status === "failed" && (
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <XCircle className="w-10 h-10 rounded-full text-red-600" />
              </div>
            )}
          </div>

          <div className="mb-8">
            {status === "checking" && (
              <>
                <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Processing Payment
                </h1>
                <p className="text-slate-500">Please wait while we verify your payment…</p>
                <p className="mt-2 text-[11px] text-slate-400">Verification attempts: {attempts}</p>
              </>
            )}

            {status === "success" && (
              <>
                <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Payment Successful!
                </h1>
                <p className="text-slate-500">
                  Your account has been upgraded to <strong>{tierName}</strong>.
                </p>
              </>
            )}

            {status === "failed" && (
              <>
                <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Payment Verification Delayed
                </h1>
                <p className="text-slate-500">
                  If Stripe completed the charge, your account may still be updating. Please try refreshing.
                </p>
              </>
            )}
          </div>

          <Button
            className="w-full bg-slate-900 text-white hover:bg-slate-800"
            onClick={() => navigate(nextPath || "/dashboard")}
            disabled={status === "checking"}
          >
            {status === "checking" ? "Please Wait…" : "Continue"}
          </Button>

          {status === "failed" && (
            <>
              <Button variant="outline" className="w-full mt-3" onClick={() => window.location.reload()}>
                Refresh This Page
              </Button>
              <Button variant="outline" className="w-full mt-3" onClick={() => navigate("/pricing")}>
                Back to Pricing
              </Button>
            </>
          )}

          <div className="mt-8 text-center text-[11px] text-slate-400">
            © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
          </div>
        </div>
      </div>
    </div>
  );
}